// /src/lib/firebase/firestore.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  setDoc
} from "firebase/firestore";
import { db } from "./config";
// Import types
import { Project, Artwork, HomePageData } from "../../types"; // types/index.ts



// Define project collection
const projectsCollection = collection(db, "projects"); // Collection reference for projects
// This collection will store all project documents

// === PROJECTS ===

export const listenForProjects = (callback: (projects: Project[]) => void) => {
  return onSnapshot(projectsCollection, (snapshot) => {
    const projects = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Project[];

    callback(projects);
  });
};

export const getProjects = async (): Promise<Project[]> => {
  const snapshot = await getDocs(projectsCollection);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Project[];
};

export const addProject = async (project: Omit<Project, "id">) => {
  await addDoc(projectsCollection, project);
};

export const updateProject = async (
  id: string,
  updatedData: Partial<Project>
) => {
  const projectDoc = doc(db, "projects", id);
  await updateDoc(projectDoc, updatedData);
};

export const deleteProject = async (id: string) => {
  const projectDoc = doc(db, "projects", id);
  await deleteDoc(projectDoc);
};

export const getProjectById = async (id: string): Promise<Project | null> => {
  const projectDoc = doc(db, "projects", id);
  const snapshot = await getDoc(projectDoc);

  if (!snapshot.exists()) return null;

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Project;
};

// === ARTWORK ===

const artworksCollection = collection(db, "artworks");

export const getArtworks = async (): Promise<Artwork[]> => {
  const snapshot = await getDocs(artworksCollection);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Artwork[];
};

export const addArtwork = async (artwork: Omit<Artwork, "id">) => {
  await addDoc(artworksCollection, artwork);
};

export const getArtworkById = async (id: string): Promise<Artwork | null> => {
  const artworkDoc = doc(db, "artworks", id);
  const snapshot = await getDoc(artworkDoc);

  if (!snapshot.exists()) return null;

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Artwork;
};

export const updateArtwork = async (
  id: string,
  updatedData: Partial<Artwork>
) => {
  const artworkDoc = doc(db, "artworks", id);
  await updateDoc(artworkDoc, updatedData);
};

export const deleteArtwork = async (id: string) => {
  const artworkDoc = doc(db, "artworks", id);
  await deleteDoc(artworkDoc);
};


// === HOMEPAGE DATA ===

export const getHomePageData = async (): Promise<HomePageData | null> => {
  const docRef = doc(db, 'settings', 'homepage');
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) return null;

  return snapshot.data() as HomePageData;
};

export const updateHomePageData = async (data: HomePageData) => {
  const docRef = doc(db, 'settings', 'homepage');
  await setDoc(docRef, data);
};